import styled from 'styled-components';
import Modal from 'react-modal';
import { OverlayImage } from 'components/Toolkit/Gallery/components/GalleryView/GalleryView.styled';
import { media } from 'helpers/breakpoints';
import { GalleryHeader } from 'components/Toolkit/Gallery/components/GalleryHeader';
import {
  Next,
  Previous,
} from 'components/Toolkit/Gallery/components/GalleryUiElements';
import { Spinner } from 'components/Toolkit/Spinner/Spinner';
import { Loading } from 'components/Toolkit/Loading/Loading';

const Img = styled.img`
  display: flex;
  max-width: 100%;
  max-height: 100%;
`;

const SModal = styled(Modal)`
  background: rgba(255, 255, 255, 0.9);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9900;

  .threeSixtyVewArea {
    display: flex;
    align-items: center;
    height: 100%;
    width: 100%;
    @media (orientation: landscape) {
      margin-top: 0;
    }
  }

  .wrapper-360 {
    position: absolute;
    width: 100%;
    overflow: hidden;
    padding-top: 56%;
  }

  .wrapper-360-video {
    width: 640px;
    height: 360px;
  }

  .fix-360-video-height {
    height: 100% !important;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  .fix-360-height {
    height: 100% !important;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  ${Previous} {
    height: 40px;
    width: 40px;
    svg {
      height: 26px;
      width: 26px;
    }
  }

  ${Next} {
    height: 40px;
    width: 40px;
    svg {
      height: 26px;
      width: 26px;
    }
  }

  .swiper-main {
    height: 100%;
    width: 100%;
    .swiper-slide {
      display: flex;
      align-content: center;
      justify-content: center;
      align-items: center;
    }
  }

  .swiper-carousel {
    height: 64px;
    margin-right: auto;
    margin-left: 0;

    ${media.medium} {
      height: 88px;
    }

    .swiper-slide {
      width: 86px;
      height: 100%;
      box-sizing: border-box;
      border: 2px solid transparent;
      ${media.medium} {
        width: 120px;
      }
    }

    .swiper-slide-thumb-active {
      border: 2px solid ${({ theme }) => theme.colors.BLUE};
    }

    ${OverlayImage} {
      object-fit: cover;
    }
  }
`;
const ModalButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;

  button:first-child,
  a:first-child {
    margin-right: ${({ theme }) => theme.spacing.S8};
  }
`;
const CarouselArrow = styled.div`
  display: none;
  ${media.large} {
    width: ${({ theme }) => theme.spacing.L48};
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex: 0 0 auto;
    max-width: 100%;
    min-width: 0;
    visibility: hidden;

    ${Previous} {
      left: auto;
      right: 16px;
    }

    ${Next} {
      left: 16px;
      right: auto;
    }
  }
`;
const CarouselWrapper = styled.div`
  flex-flow: row wrap;
  display: flex;
  padding: ${({ theme }) => `${theme.spacing.S8}`};
  width: 100%;

  @media (orientation: landscape) {
    align-items: flex-start;
  }

  ${media.large} {
    max-height: 100%;
    padding: ${({ theme }) => `${theme.spacing.S8} 0 ${theme.spacing.M24} 0`};
    align-items: center;
    align-self: stretch;
    padding: ${({ theme }) => `${theme.spacing.S8}`};
  }
`;

const ImageLoader = styled(Loading)`
  margin-left: -12px;
  margin-top: -12px;
  border: 0px;
`;

const CarouselContent = styled.div`
  flex: 1 1 0px;
  width: 1px;
  min-width: 0;
  display: flex;
  align-items: center;
`;
const TopBanner = styled.div`
  flex: 0 0 auto;
  grid-area: banner;
  margin-bottom: ${({ theme }) => theme.spacing.S8};
  display: flex;
  align-items: center;
  justify-content: center;
  /* On mobile we hide the banner when the user rotates phone */
  @media (orientation: landscape) {
    display: none;
  }

  ${media.large} {
    display: none;
  }
`;
const SideBanner = styled.div`
  display: none;

  ${media.large} {
    display: flex;
    // Largest DFP unit is 300px + 40px gap
    width: 340px;
    flex: 0 0 auto;
    justify-content: center;
    align-items: center;
    padding-left: 40px;
    .mpu {
      display: none;
    }
  }

  ${media.largeWithHeight(746)} {
    .tall {
      display: none;
    }
    .mpu {
      display: flex;
    }
  }
`;
const Container = styled.div<{ sidebar: boolean }>`
  background: #fff;
  height: 100%;
  // Fallback to flex for older mobile browsers
  display: flex;
  flex-direction: column;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: auto auto 1fr auto;
  grid-template-areas:
    'banner'
    'header'
    'body'
    'footer';
`;
const Header = styled(GalleryHeader)`
  flex: 0 0 auto;
  grid-area: header;
  ${media.large} {
    height: 72px;
    top: 0;
    padding: ${({ theme }) => `${theme.spacing.S8} ${theme.spacing.L40}`};
  }
  ${media.mediumOnly} {
    @media (orientation: landscape) {
      display: none;
    }
  }
`;
const Body = styled.div`
  position: relative;
  flex: 1 1 auto;
  flex-direction: column; // Fixes bug on older ios safari
  overflow: hidden;
  grid-area: body;
  display: flex;
  width: 100%;

  ${media.large} {
    flex-direction: row;
    padding: ${({ theme }) => `0 ${theme.spacing.L40}`};
  }
`;
const Footer = styled.div`
  flex: 0 0 auto;
  grid-area: footer;
  width: 100%;
  max-width: 100%;
  padding: 0px;

  ${media.mediumOnly} {
    @media (orientation: landscape) {
      display: none;
    }
  }

  ${media.large} {
    width: calc(100% - 340px);
    align-items: flex-start;
    padding-left: 40px;
    padding-right: 40px;
  }

  ${media.largeWithHeight(746)} {
    .tall {
      display: none;
    }
    .mpu {
      display: flex;
    }
  }

  :hover {
    ${CarouselArrow} {
      visibility: visible;
    }
  }
`;
const SSpinner = styled(Spinner)`
  position: absolute;
  right: 0;
  left: 0;
  z-index: -1;
`;
const Count = styled.div`
  ${({ theme }) => theme.fontSize.L18};
  color: ${({ theme }) => theme.colors.GREY_DARKER};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  position: absolute;
  left: ${({ theme }) => theme.spacing.S12};
  top: ${({ theme }) => theme.spacing.S8};
  display: none;

  ${media.mediumOnly} {
    @media (orientation: landscape) {
      display: block;
    }
  }
`;
const Close = styled.span`
  color: ${({ theme }) => theme.colors.GREY_DARK};
  cursor: pointer;
  flex: 1;
  text-align: right;
  position: absolute;
  display: none;
  right: ${({ theme }) => theme.spacing.S12};
  top: ${({ theme }) => theme.spacing.S8};

  ${media.mediumOnly} {
    @media (orientation: landscape) {
      display: block;
    }
  }
`;
const MainViewWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  flex: 1 1 auto;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  margin: 0 auto;
  flex-direction: column;
  max-width: 1016px;
  padding: 0px;

  @media (orientation: landscape) {
    align-items: flex-start;
    max-width: 85%;
  }

  /* YouTube Specific */
  .youtube {
    width: 100%;
    height: auto;
    display: flex;
  }

  ${Previous} {
    left: ${({ theme }) => theme.spacing.S4};
  }

  ${Next} {
    right: ${({ theme }) => theme.spacing.S4};
  }

  ${media.mediumOnly} {
    @media (orientation: landscape) {
      max-height: 100%;
      padding: 0 ${({ theme }) => theme.spacing.L56};
      ${Previous} {
        left: ${({ theme }) => theme.spacing.S4};
        visibility: visible;
      }

      ${Next} {
        right: ${({ theme }) => theme.spacing.S4};
        visibility: visible;
      }
    }
  }

  ${media.large} {
    max-height: 100%;
    padding: 0 ${({ theme }) => theme.spacing.L56};
    width: calc(100% - 340px);

    ${Previous} {
      left: 0px;
      visibility: visible;
    }

    ${Next} {
      right: 0px;
      visibility: visible;
    }
  }

  // 360 Videos
  .threeSixtyVewArea {
    display: flex;
    align-items: center;
    height: 100%;
    width: 100%;
    @media (orientation: landscape) {
      margin-top: 0;
    }
  }
`;

export {
  Img,
  SModal,
  ModalButtonGroup,
  TopBanner,
  SideBanner,
  Container,
  Header,
  Body,
  Footer,
  CarouselArrow,
  CarouselContent,
  CarouselWrapper,
  SSpinner,
  Count,
  Close,
  MainViewWrapper,
  ImageLoader,
};
